import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Chip, Grid } from "@mui/material";
import { uniqueId } from "lodash";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";

import { get, isAdmin, post } from "../../utils/io";
import MainContentContainer from "../Layouts/MainContentContainer";
import AttachPOModal from "./AttachPOModal";
import PurchaseOrderSearchForm, {
  emptySearchInputs,
  STATUS_COLORS,
} from "./PurchaseOrderSearchForm";
import StelDataGrid from "components/StelDataGrid";
import CreateOrderModal from "components/StelModal/CreateOrderModal";

function PurchaseOrders() {
  const [orders, setOrders] = useState([]);
  const [searchFields, setSearchFields] = useState(emptySearchInputs);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [loadingOrderReceived, setLoadingOrderReceived] = useState(null);
  const [attachPOModalOpen, setAttachPOModalOpen] = useState(false);
  const [selectedOrderForPO, setSelectedOrderForPO] = useState(null);
  const [attachingPO, setAttachingPO] = useState(false);
  const [isCreatePOOpen, setIsCreatePOOpen] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const loadOrders = async () => {
    setLoading(true);
    const params = {
      order_id: searchFields.id,
      supplier: searchFields.supplier,
      status: searchFields.status,
      offset: paginationModel.pageSize * paginationModel.page,
      limit: paginationModel.pageSize,
    };

    const res = isAdmin()
      ? await get("admin/purchase_orders", params)
      : await get("/sales_orders", params);

    if (res.status === 200) {
      setLoading(false);
      setTotalCount(res.data.count);
      return setOrders(
        !isAdmin() ? res.data.sales_orders : res.data.purchase_orders
      );
    }
    setLoading(false);
    return setOrders([]);
  };

  const handleConfirmReceipt = async (orderId) => {
    setLoadingOrderReceived(orderId);
    try {
      const res = await post(`/sales_orders/${orderId}/confirm_receipt`);
      if (res.status === 204) {
        enqueueSnackbar("Order receipt confirmed successfully", {
          variant: "success",
        });
        loadOrders();
      } else {
        enqueueSnackbar("Failed to confirm order receipt", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error confirming order receipt", { variant: "error" });
    }
    setLoadingOrderReceived(null);
  };

  const handleOpenAttachPOModal = (orderId) => {
    setSelectedOrderForPO(orderId);
    setAttachPOModalOpen(true);
  };

  const handleCloseAttachPOModal = () => {
    setAttachPOModalOpen(false);
    setSelectedOrderForPO(null);
  };

  const handleAttachPO = async ({ poNumber, poFile }) => {
    setAttachingPO(true);
    try {
      const formData = new FormData();
      formData.append("po_number", poNumber);
      formData.append("po_file", poFile);

      const res = await post(
        `/sales_orders/${selectedOrderForPO}/attach_po`,
        formData
      );

      if (res.status === 200) {
        enqueueSnackbar("Purchase Order attached successfully", {
          variant: "success",
        });
        handleCloseAttachPOModal();
        loadOrders();
      } else {
        enqueueSnackbar("Failed to attach Purchase Order", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error attaching Purchase Order", { variant: "error" });
    }
    setAttachingPO(false);
  };

  const renderLineItems = (lineItems) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      {lineItems.map((item, idx) => (
        <Box
          key={uniqueId(item.id)}
          sx={{
            padding: 1,
            margin: 0,
            display: "flex",
            gap: 1,
            ...(idx > 0 && {
              borderTop: "1px solid",
              borderColor: "divider",
            }),
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
              }}
            >
              <Box
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: 14,
                }}
              >
                {item.quantity}x
              </Box>
              <Box
                sx={{
                  fontSize: 14,
                }}
              >
                {item.name}
              </Box>
            </Box>
            <Box
              sx={{
                fontWeight: "bold",
                fontSize: 14,
              }}
            >
              {item?.unit_price ? `$${item.unit_price.toFixed(2)}` : "-"}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );

  const renderStatusCell = (status) => {
    const statusColors = STATUS_COLORS[status] || {
      backgroundColor: "#F5F5F5",
      color: "#000000",
      borderColor: "#000000",
    };

    return (
      <Chip
        sx={{
          my: "4px",
          color: statusColors.color,
          backgroundColor: statusColors.backgroundColor,
          borderColor: statusColors.borderColor,
        }}
        label={status}
        variant="outlined"
        size="small"
      />
    );
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.8 },
    ...(isAdmin()
      ? [
          {
            field: "supplier_name",
            headerName: "Manufacturer",
            flex: 1,
            renderCell: ({ value }) => value || "-",
          },
        ]
      : []),
    {
      field: "total_quantity",
      headerName: "Quantity",
      flex: 0.6,
      renderCell: ({ value }) => value || 0,
    },
    {
      field: "line_items",
      headerName: "Item",
      cellClassName: "col-device",
      flex: 2.5,
      renderCell: ({ value }) => renderLineItems(value || []),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1.5,
      renderCell: (param) =>
        renderStatusCell(param?.row?.status_custom_label || param?.row?.status),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
    },
    {
      field: "total_amount",
      headerName: "Total Amount",
      flex: 1,
      renderCell: ({ value }) => `$${parseFloat(value || 0).toFixed(2)}`,
    },
    ...(!isAdmin()
      ? [
          {
            field: "action",
            headerName: "Action",
            flex: 1,
            renderCell: ({ row }) => {
              if (
                row.status_custom_label === "Delivery Confirmed" &&
                row.shipment_date
              ) {
                return (
                  <Chip
                    sx={{ my: "4px" }}
                    color="success"
                    label={`Received on ${new Date(
                      row.shipment_date
                    ).toLocaleDateString()}`}
                  />
                );
              }

              if (row.status === "Shipped") {
                return (
                  <Button
                    sx={{ my: "4px" }}
                    size="small"
                    variant="contained"
                    loading={loadingOrderReceived === row.id}
                    onClick={() => handleConfirmReceipt(row.id)}
                  >
                    Order Received
                  </Button>
                );
              }

              if (
                row.status_custom_label === "Estimate Sent" &&
                !row.po_file_attached
              ) {
                return (
                  <Button
                    sx={{ my: "4px" }}
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => handleOpenAttachPOModal(row.id)}
                  >
                    Attach PO
                  </Button>
                );
              }

              if (
                row.status_custom_label === "Estimate Sent" &&
                row.po_file_attached
              ) {
                return (
                  <Chip
                    sx={{ my: "4px" }}
                    color="info"
                    label="PO attached"
                    variant="outlined"
                    size="small"
                  />
                );
              }

              return null;
            },
          },
        ]
      : []),
  ];

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFields, paginationModel]);

  return (
    <MainContentContainer title="Purchase Orders">
      <Grid container direction="column">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <PurchaseOrderSearchForm
            onSubmit={setSearchFields}
            isSearchDisabled={loading}
          />
        </Box>

        {isAdmin() && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              sx={{ height: "fit-content", mb: "15px" }}
              color="primary"
              variant="contained"
              onClick={() => setIsCreatePOOpen(true)}
              startIcon={<AddIcon />}
            >
              Create
            </Button>
          </Box>
        )}

        <StelDataGrid
          columns={columns}
          rows={orders}
          loading={loading}
          getRowHeight={() => "auto"}
          sx={{
            "& .MuiDataGrid-cell.col-device": {
              px: 0,
              py: 0,
              borderRight: "1px solid #0000003B",
              borderLeft: "1px solid #0000003B",
            },
          }}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          rowCount={totalCount}
        />
      </Grid>

      <AttachPOModal
        open={attachPOModalOpen}
        onClose={handleCloseAttachPOModal}
        onSubmit={handleAttachPO}
        loading={attachingPO}
      />

      <CreateOrderModal
        open={isCreatePOOpen}
        onClose={() => setIsCreatePOOpen(false)}
        onSuccess={loadOrders}
      />
    </MainContentContainer>
  );
}

export default PurchaseOrders;
